import {Component, computed, inject, input, linkedSignal, output} from '@angular/core';
import {rxResource} from '@angular/core/rxjs-interop';
import {DirectusService} from '@frontend/directus';
import {CardModule} from 'primeng/card';
import {ButtonModule} from 'primeng/button';
import {TooltipModule} from 'primeng/tooltip';
import {TranslocoPipe, translate} from '@jsverse/transloco';
import {ProviderTenderView, TenderStatus, CustomerStatus} from '@frontend/shared-api';
import {TenderCardSelectFormComponent} from '../contact-select-form/tender-card-contact-select-form.component';
import {ProviderTenderCardChatComponent} from '../provider-tender-card-chat/provider-tender-card-chat.component';
import {TenderPhaseComponent} from '../tender-phase/tender-phase.component';
import {DirectusTooltipComponent} from '../../../../../shared/components/directus-tooltip/directus-tooltip.component';
import {TenderPhaseUtils} from '../../../../../phase/tender-phase.util';
import {TenderStatusUtils} from '../../../../../phase/tender-status.util';
import {TranslateDirectusPipe} from '@frontend/transloco';
import {TenderCardContactPersonFormData} from '../contact-select-form/tender-card-contact-select-form.types';
import {TenderCardActionButtonVM} from './tender-card.types';
import {AppDatePipe} from '@frontend/shared';

@Component({
  selector: 'app-tender-card',
  imports: [
    CardModule,
    ButtonModule,
    TenderCardSelectFormComponent,
    ProviderTenderCardChatComponent,
    TenderPhaseComponent,
    TranslocoPipe,
    TooltipModule,
    DirectusTooltipComponent,
    TranslateDirectusPipe,
    AppDatePipe,
  ],
  templateUrl: './tender-card.component.html',
  styleUrl: './tender-card.component.scss',
})
export class TenderCardComponent {
  private readonly directusService = inject(DirectusService);

  readonly tender = input.required<ProviderTenderView>();

  private readonly hrToolsMiniResource = rxResource({
    stream: () => this.directusService.loadHrToolsMini(),
  });

  contractPerson = computed(() => {
    return {
      tenderContactPersonUuid: this.tender().contactPerson?.providerUserUuid,
    } as TenderCardContactPersonFormData;
  });

  protected readonly hrToolTranslations = computed(() => {
    const id = this.tender().hrToolDirectusId;
    return this.hrToolsMiniResource.value()?.find((t) => t.id === id)?.translations;
  });

  protected readonly hasContactPerson = linkedSignal(
    () => !!this.tender().contactPerson?.providerUserUuid,
  );

  readonly answerRequest = output<ProviderTenderView>();

  protected readonly customerStatusLabel = computed(() => {
    const customerStatus = this.tender().customerStatus;
    const currentTenderPhase = this.tender().activePhaseStatusInfo.phase;
    switch (customerStatus) {
      case CustomerStatus.AWAITING_PROVIDER_RESPONSE:
        return translate('provider.tenders.customerStatusWaiting', {phase: currentTenderPhase});
      case CustomerStatus.NEXT_PHASE_STARTED:
        return translate('provider.tenders.customerStatusPreparation', {
          phase: TenderPhaseUtils.getNextPhase(currentTenderPhase),
        });
      case CustomerStatus.TENDER_CLOSED:
        return translate('provider.tenders.customerStatusTenderClosed');
    }
  });

  protected readonly actionButton = computed<TenderCardActionButtonVM>(() => {
    const currentStatus = this.tender().activePhaseStatusInfo.status;
    if (currentStatus === TenderStatus.INVITED) {
      return {visible: true, labelKey: 'provider.tenders.checkParticipation'};
    }
    if (TenderStatusUtils.canEditQuestionnaire(currentStatus)) {
      return {visible: true, labelKey: 'provider.tenders.editPhase'};
    }
    return {visible: false, labelKey: ''};
  });

  protected onAnswerRequest(): void {
    this.answerRequest.emit(this.tender());
  }
}
